import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  getDefaultRoutePath,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAppContext } from "../../../../AppContext";
import { IImpersonationLoginResponseModel } from "../models/impersonationLoginResponseModel";
import {
  buildImpersonationLandingPath,
  getImpersonationErrorMessage,
  IMPERSONATION_MISSING_TICKET_ERROR,
  readImpersonationTicket,
} from "../utilities/impersonationUtility";

/**
 * Landing of the admin panel's "log into this profile" link: redeems the one-time ticket and
 * signs in as the user, replacing whatever session this browser had for the app.
 */
export const useImpersonationLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const signIn = useSignIn();
  const queryClient = useQueryClient();
  const { resetStudios } = useAppContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The ticket works once; React's dev double-run of effects must not spend it twice.
  const hasStartedRef = useRef(false);

  // Handlers on the hook, not on mutate(): those belong to the mutation itself and still run
  // after StrictMode's dev remount, which drops the per-call ones.
  const { mutate } = useApiMutation<IImpersonationLoginResponseModel>({
    onSuccess: (user) => {
      // Nothing cached for the previous session may leak into the user's.
      queryClient.clear();
      resetStudios();

      // Like every other sign-in, the permissions go to localStorage: with them the auth-state
      // cookie outgrows the browser's 4 KB limit, so it would be dropped and the session lost
      // (or the previous user's state kept) on the next reload.
      const { permissions, ...userWithoutPermissions } = user;
      localStorage.setItem("permissions", JSON.stringify(permissions));

      const isSignedIn = signIn({
        auth: { token: user.authToken, type: "Bearer" },
        userState: userWithoutPermissions,
      });

      if (isSignedIn) {
        navigate(
          buildImpersonationLandingPath(getDefaultRoutePath(user), user.studioId),
          { replace: true },
        );
      }
    },
    onError: (error) => setErrorMessage(getImpersonationErrorMessage(error)),
  });

  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    const ticket = readImpersonationTicket(location.hash);

    // Take the ticket out of the address bar and the history right away.
    window.history.replaceState(window.history.state, "", location.pathname);

    if (!ticket) {
      setErrorMessage(IMPERSONATION_MISSING_TICKET_ERROR);
      return;
    }

    mutate({
      endpoint: endpointsMetadata.authentication.impersonationLogin,
      method: "post",
      payload: { ticket },
      disableSuccessToast: true,
      disableErrorToast: true,
      disable401Redirect: true,
    });
  }, []);

  const goToStart = () => navigate(routePaths.home, { replace: true });

  return { errorMessage, goToStart };
};
